import { Injectable, inject } from '@angular/core';
import { uniqBy } from 'lodash-es';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseNestedFieldDefinition, DottedKey } from '../../../core/field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../../core/field-definitions/helpers/field-definition.helper';
import { UserService } from '../../../core/users/services/user.service';
import { ShFieldDefinitionService } from '../../field-definitions/services/field-definition.service';
import { UserFunction } from '../../users/entities/user-functions.entity';
import { ExtendedProfileField } from '../entities/extended-profile-field.entity';
import { ExtendedFacetProfile, ExtendedProfile } from '../entities/extended-profile.entity';
import { ProfileField, ProfileFieldScope } from '../entities/profile-field.entity';
import { Profile } from '../entities/profile.entity';

@Injectable({ providedIn: 'root' })
export class ProfileHelper {
    // Injectors
    private readonly userService: UserService = inject(UserService);
    private readonly fieldDefinitionService: ShFieldDefinitionService = inject(ShFieldDefinitionService);

    // TODO: Move extended profile service code here
    // #region Filtering
    public filterViewableProfiles<T extends Profile>(allProfiles: T[]): Observable<T[]> {
        return combineLatest([
            this.userService.hasUserFunction(UserFunction.viewStandardFields),
            this.userService.hasUserFunction(UserFunction.viewAdvancedFields)
        ]).pipe(
            take(1),
            map(([viewStandard, viewAdvanced]) => {
                const profiles: T[] = [];

                allProfiles.forEach(profile => {
                    const filteredProfile: T = this.filterViewableProfileFields(profile, viewStandard, viewAdvanced);

                    if (profile.Fields.length === 0 || filteredProfile.Fields.length > 0) {
                        profiles.push(filteredProfile);
                    }
                });

                return profiles;
            })
        );
    }

    private filterViewableProfileFields<T extends Profile>(
        profile: T,
        viewStandard: boolean,
        viewAdvanced: boolean
    ): T {
        const profileFields: ProfileField[] = profile.Fields.filter(field => {
            if (field.Scope === ProfileFieldScope.Standard) {
                return viewStandard;
            }
            if (field.Scope === ProfileFieldScope.Advanced) {
                return viewAdvanced;
            }

            return true;
        });

        profile.Fields = profileFields;
        return profile;
    }

    public filterByLanguage<T extends ExtendedProfile | ExtendedFacetProfile>(profiles: T[]): Observable<T[]> {
        return this.userService.getCurrentUser().pipe(
            map(user => user.Locale.split('_')[0]),
            map(lang =>
                profiles.map(profile => ({
                    ...profile,
                    Fields: this.filterFieldsByLanguage(profile.Fields, lang)
                }))
            )
        );
    }

    private filterFieldsByLanguage(fields: ExtendedProfileField[], userLang: string): ExtendedProfileField[] {
        return fields.filter(field => !field.FieldDefinition.Lang || field.FieldDefinition.Lang === userLang);
    }
    // #endregion

    // #region Extending
    public extendProfiles(profiles: Profile[]): Observable<ExtendedProfile[]> {
        const dottedKeys: DottedKey[] = profiles.flatMap(profile => profile.Fields.map(field => field.DottedKey));
        return this.fieldDefinitionService
            .getByProfileIds(
                profiles.map(profile => profile.Id),
                dottedKeys
            )
            .pipe(map(fieldDefinitions => profiles.map(profile => this.extendProfile(profile, fieldDefinitions))));
    }

    private extendProfile(profile: Profile, fieldDefinitions: BaseNestedFieldDefinition[]): ExtendedProfile {
        if (!profile.Fields || profile.Fields.length === 0) {
            return profile as ExtendedProfile;
        }

        const extendedProfileFields: ExtendedProfileField[] = profile.Fields.map(field =>
            this.extendProfileField(field, fieldDefinitions)
        );
        return {
            ...profile,
            Fields: extendedProfileFields
        };
    }

    private extendProfileField(
        profileField: ProfileField,
        fieldDefinitions: BaseNestedFieldDefinition[]
    ): ExtendedProfileField {
        const rootKey: DottedKey | undefined = FieldDefinitionHelper.getDottedKeyRoot(profileField.DottedKey);
        const rootFieldDefinition: BaseNestedFieldDefinition | undefined = fieldDefinitions.find(
            fieldDefinition => fieldDefinition.DottedKey === rootKey
        );

        if (!rootFieldDefinition) {
            console.error(`Root fieldDefinition ${profileField.DottedKey} was not found`);
        }

        const profileFieldDefinition: BaseNestedFieldDefinition | undefined = rootFieldDefinition
            ? FieldDefinitionHelper.getNestedByDottedKey(rootFieldDefinition, profileField.DottedKey)
            : undefined;

        if (!profileFieldDefinition) {
            console.error(`FieldDefinition ${profileField.DottedKey} was not found`);
        }

        return {
            ...profileField,
            FieldDefinition: profileFieldDefinition
        } as ExtendedProfileField;
    }

    // #endregion

    // #region Util
    public mergeProfiles<T extends Profile>(profiles: T[]): T | null {
        if (profiles.length === 0) {
            return null;
        }

        return {
            ...profiles[0],
            Fields: uniqBy(
                profiles.flatMap(profile => profile.Fields),
                field => field.DottedKey
            )
        };
    }
}
